import { App } from '@capacitor/app';
import { authLogger } from './logger';
import { isNativeApp, NATIVE_AUTH_CALLBACK_PATH } from './nativePlatform';
import { recordNativeDiagnostic } from './nativeDiagnostics';

const describeAuthResponse = (parsedUrl: URL): string => {
  const queryHasCode = parsedUrl.search.includes('code=');
  const queryHasError = parsedUrl.search.includes('error=');
  const hashHasCode = parsedUrl.hash.includes('code=');
  const hashHasError = parsedUrl.hash.includes('error=');
  return `query{code:${queryHasCode},error:${queryHasError}} hash{code:${hashHasCode},error:${hashHasError}}`;
};

const toInternalPath = (url: string): string | null => {
  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.protocol === 'renewmyday:' && parsedUrl.hostname === 'auth' && parsedUrl.pathname === '/callback') {
      recordNativeDiagnostic('Auth callback received', describeAuthResponse(parsedUrl));
      return `${NATIVE_AUTH_CALLBACK_PATH}${parsedUrl.search}${parsedUrl.hash}`;
    }

    const hostnamePath = parsedUrl.hostname ? `/${parsedUrl.hostname}` : '';
    const path = parsedUrl.pathname && parsedUrl.pathname !== '/' ? parsedUrl.pathname : hostnamePath;

    if (!path.startsWith('/admin') && !path.startsWith('/login')) {
      return null;
    }

    return `${path}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    authLogger.warn('Unable to parse native deep link URL', error);
    recordNativeDiagnostic('Failed to parse deep link', error);
    return null;
  }
};

// Tracks the auth callback (by authorization code) we have already routed so the
// same callback is not processed twice. iOS can deliver the deep link both via
// App.getLaunchUrl() (cold start) and App.addListener('appUrlOpen'), and routing
// the auth callback triggers a hard WebView reload. Without this guard those
// duplicate deliveries cause repeated reloads, which appears as the screen
// continuously blinking between the login and app views.
const HANDLED_AUTH_CALLBACK_KEY = 'native_auth_callback_handled';

const getAuthCallbackIdentifier = (internalPath: string): string | null => {
  try {
    const params = new URLSearchParams(internalPath.split('?')[1] ?? '');
    return params.get('code') || params.get('error') || null;
  } catch {
    return null;
  }
};

const wasAuthCallbackHandled = (identifier: string): boolean => {
  try {
    return sessionStorage.getItem(HANDLED_AUTH_CALLBACK_KEY) === identifier;
  } catch {
    return false;
  }
};

const markAuthCallbackHandled = (identifier: string): void => {
  try {
    sessionStorage.setItem(HANDLED_AUTH_CALLBACK_KEY, identifier);
  } catch {
    /* sessionStorage unavailable — best-effort dedupe only */
  }
};

const routeNativeUrl = (url: string): void => {
  recordNativeDiagnostic('Native URL open', url);
  const internalPath = toInternalPath(url);

  if (!internalPath) {
    authLogger.debug('Ignoring native deep link outside supported app paths', { url });
    recordNativeDiagnostic('Ignored deep link (unsupported path)');
    return;
  }

  if (internalPath.startsWith(NATIVE_AUTH_CALLBACK_PATH)) {
    const identifier = getAuthCallbackIdentifier(internalPath);

    // Skip duplicate deliveries of the same callback to avoid reload loops/blinking.
    if (identifier && wasAuthCallbackHandled(identifier)) {
      authLogger.debug('Ignoring already-handled auth callback', { identifier });
      recordNativeDiagnostic('Ignored duplicate auth callback', identifier);
      return;
    }

    if (identifier) {
      markAuthCallbackHandled(identifier);
    }

    // If we are already on the callback path with these same params (e.g. the
    // launch URL fired after the reload already landed us here), don't reload again.
    const currentSearch = window.location.search;
    if (
      window.location.pathname === NATIVE_AUTH_CALLBACK_PATH &&
      identifier &&
      currentSearch.includes(identifier)
    ) {
      authLogger.debug('Auth callback already loaded in WebView, skipping reload');
      recordNativeDiagnostic('Auth callback already loaded, no reload');
      return;
    }

    recordNativeDiagnostic('Routing to auth callback', internalPath);
    window.location.assign(internalPath);
    return;
  }

  window.history.pushState({}, '', internalPath);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export const initializeNativeDeepLinks = (): void => {
  if (!isNativeApp()) {
    return;
  }

  recordNativeDiagnostic('App boot', {
    path: window.location.pathname,
    hasQueryCode: window.location.search.includes('code='),
    hasHashCode: window.location.hash.includes('code=')
  });

  App.getLaunchUrl().then((launchUrl) => {
    if (launchUrl?.url) {
      routeNativeUrl(launchUrl.url);
    }
  }).catch((error) => {
    authLogger.error('Failed to read native launch URL', error);
  });

  App.addListener('appUrlOpen', ({ url }) => {
    routeNativeUrl(url);
  }).catch((error) => {
    authLogger.error('Failed to register native deep link listener', error);
  });
};